import { useQuery } from '@tanstack/react-query';

import { apiClient, type IdentifierDefinitionDto } from '@/api/client';
import { queryKeys } from '@/query/keys';

/**
 * Read-only catalog of identifier definitions for the current tenant. Backed
 * by the paginated `/identifier-definitions` endpoint; we ask for a single
 * generous page since the catalog is small (typically &lt;10 rows). Same query
 * key as the admin page so admin edits invalidate this consumer too.
 */
export function useIdentifierTypes() {
  return useQuery<IdentifierDefinitionDto[]>({
    queryKey: queryKeys.identifierTypes,
    queryFn: async ({ signal }) => {
      const page = await apiClient.getIdentifierDefinitions({ size: 200, signal });
      return page.content ?? [];
    },
    staleTime: 10 * 60_000,
  });
}

/**
 * Active-only variant of {@link useIdentifierTypes}. The patient-facing
 * dropdowns (create-patient, patient-sheet IdentifiersCard) use this so a
 * deactivated identifier definition disappears from new pickers without
 * disturbing the rows that already reference it.
 */
export function useActiveIdentifierTypes() {
  return useQuery<IdentifierDefinitionDto[]>({
    queryKey: queryKeys.identifierTypesActive,
    queryFn: async ({ signal }) => {
      const page = await apiClient.getIdentifierDefinitions({ size: 200, active: true, signal });
      return page.content ?? [];
    },
    staleTime: 10 * 60_000,
  });
}
